import { CopyId, Money, Timestamp } from '@/components/primitives';
import { Explain } from '@/components/Explain';
import { JsonCell } from '@/components/JsonCell';
import { cn } from '@/lib/cn';
import {
  formatDecimal,
  formatIp,
  parseClickhouseTime,
  parseMysqlTime,
  titleize,
  type TimeZoneMode,
} from '@/lib/format';
import type { CurrencyMetadataRow } from '@/types';

/**
 * Generic renderer for row collections whose exact columns vary by source (and,
 * for per-game `bets`, by game). It infers columns from the data rather than
 * hard-coding a schema, then renders each value by what the field MEANS:
 * amounts through currency metadata, ips decoded, timestamps normalised, ids
 * copyable.
 */

export type TimeKind = 'mysql' | 'clickhouse';

const AMOUNT_FIELDS = new Set([
  'amount',
  'bet_amount',
  'won_amount',
  'cashout_amount',
  'balance',
  'max_win',
]);
const ID_FIELDS = /(^id$|_id$|_uniq_id$|trx_id$)/;
const TIME_FIELDS = /(^dt$|_at$|_date$|_try$|^created$|^updated$)/;
const IP_FIELDS = new Set(['player_ip']);
/** decimal(38,18) that is NOT money — trim rather than route through currency. */
const DECIMAL_FIELDS = /(odd|coefficient|multiplier|^rtp$)/i;

export function DataTable({
  rows,
  meta,
  tz,
  timeKind = 'mysql',
  currencyField = 'currency',
  hide,
}: {
  rows: Record<string, unknown>[];
  meta?: CurrencyMetadataRow | null;
  tz: TimeZoneMode;
  timeKind?: TimeKind;
  currencyField?: string;
  hide?: string[];
}) {
  if (!rows.length) return null;

  const hidden = new Set(hide ?? []);
  // Union of keys — sources are not guaranteed to be uniform row to row.
  const columns = Array.from(
    rows.reduce<Set<string>>((set, r) => {
      Object.keys(r).forEach((k) => set.add(k));
      return set;
    }, new Set()),
  ).filter((c) => !hidden.has(c));

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse whitespace-nowrap text-[12px]">
        <thead>
          <tr className="border-b border-hairline bg-surface-sunken">
            {columns.map((c) => (
              <th key={c} className="eyebrow px-2.5 py-1.5 text-left font-semibold">
                <Explain term={c}>{titleize(c)}</Explain>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-hairline/50 last:border-0 hover:bg-surface-raised/50">
              {columns.map((c) => (
                <td key={c} className="px-2.5 py-1.5 align-top">
                  <Value
                    field={c}
                    value={row[c]}
                    meta={meta}
                    currency={row[currencyField] as string | undefined}
                    tz={tz}
                    timeKind={timeKind}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Value({
  field,
  value,
  meta,
  currency,
  tz,
  timeKind,
}: {
  field: string;
  value: unknown;
  meta?: CurrencyMetadataRow | null;
  currency?: string;
  tz: TimeZoneMode;
  timeKind: TimeKind;
}) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-text-faint">—</span>;
  }

  if (AMOUNT_FIELDS.has(field)) {
    return <Money amount={value as string} meta={meta} currency={currency} />;
  }

  if (IP_FIELDS.has(field)) {
    const ip = formatIp(value as number);
    return <span className="font-mono text-text-dim">{ip ?? '—'}</span>;
  }

  if (DECIMAL_FIELDS.test(field)) {
    return <span className="tnum font-mono text-text">{formatDecimal(value)}</span>;
  }

  if (TIME_FIELDS.test(field) && (typeof value === 'string' || value instanceof Date)) {
    const raw = String(value);
    const date =
      timeKind === 'clickhouse' ? parseClickhouseTime(raw) : parseMysqlTime(raw);
    return <Timestamp date={date} raw={raw} mode={tz} withDate />;
  }

  // JSON columns — notably delay_delivery_tries.payload, the exact transaction
  // we tried to deliver to the partner. Never a truncated preview: support has
  // to read the whole thing and paste it into a ticket.
  if (typeof value === 'object') {
    return <JsonCell value={value} />;
  }

  const str = String(value);

  if (ID_FIELDS.test(field)) {
    return <CopyId value={str} truncate={str.length > 22} />;
  }

  if (field === 'action' || field === 'status') {
    return (
      <span
        className={cn(
          'font-condensed text-[10px] font-semibold uppercase tracking-wider',
          str === 'win' || str === 'active' || str === 'OK'
            ? 'text-ok'
            : str === 'rollback' || str === 'pending'
              ? 'text-warn'
              : 'text-text-dim',
        )}
      >
        {str}
      </span>
    );
  }

  if (field === 'code') {
    return (
      <span className={cn('font-mono', str === 'OK' ? 'text-ok' : 'text-crit')}>{str}</span>
    );
  }

  if (field === 'client_error') {
    const failed = str !== 'false' && str !== '';
    return <span className={failed ? 'text-crit' : 'text-text-faint'}>{str}</span>;
  }

  return <span className="font-mono text-text">{str}</span>;
}
